import {
  FacebookSettingKey,
  SettingKey,
} from '$extensionSrc/recommendations/recommendationSettingsKeys'

import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  allPrivacySettingsFriendsOrStricter,
  getUniquePrivacyLevelCounts,
} from './facebookRecommendationCardDisplayUtils'
import {
  clickOnPhotoById,
  gatherEditPrivacyIconValue,
  getPhotoIdFromCurrentUrl,
  getVisiblePhotoUrls,
  goToAlbum,
  hasAlbum,
  scrollToBottom,
  setEditPrivacyIconValueAsStrictOrStricter,
  setEditPrivacyIconValueExact,
  waitForAlbumsToLoad,
} from './fbAlbumPhotoViewUtils'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import { albumsUrl } from './fbUrls'
import { FACEBOOK_PHOTO_STAGE_TIMEOUT } from '../../consts/stageMachineConsts'
import { FBRecommendationKey, StageType } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import * as promises from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { getInitialValueBySettingKey } from '../utils'

// Setting property functions
const OLD_INITIAL_VALUE_KEY = 'profilePictureSettings'
const INITIAL_VALUE_KEY: SettingKey = FacebookSettingKey.PROFILE_PHOTO_PRIVACY

function getCurrentValueProfile(initialState): string | null {
  if (initialState === null || initialState === undefined) {
    return null
  }

  if (
    initialState[INITIAL_VALUE_KEY] === null ||
    initialState[INITIAL_VALUE_KEY] === undefined
  ) {
    return 'No photos'
  }

  if (Object.keys(initialState[INITIAL_VALUE_KEY]).length === 0) {
    return 'No photos'
  }

  if (initialState[INITIAL_VALUE_KEY]) {
    return getUniquePrivacyLevelCounts(initialState[INITIAL_VALUE_KEY])
  }

  return null
}

function getIsFulfilledProfile(initialState): boolean {
  if (initialState === null || initialState === undefined) {
    return false
  }

  if (
    initialState[INITIAL_VALUE_KEY] === null ||
    initialState[INITIAL_VALUE_KEY] === undefined ||
    Object.keys(initialState[INITIAL_VALUE_KEY]).length === 0
  ) {
    return true
  }

  if (initialState[INITIAL_VALUE_KEY]) {
    return allPrivacySettingsFriendsOrStricter(initialState[INITIAL_VALUE_KEY])
  }

  return false
}

const getCurrentValueFn = () => getCurrentValueProfile
const getIsFulfilledFn = () => getIsFulfilledProfile
const getCantUndoFn = () => defaultCantUndoFn
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

// Automations
enum StageKey {
  GATHER_SETTINGS_PROFILE_PHOTO_PRIVACY = 'GATHER_SETTINGS_PROFILE_PHOTO_PRIVACY',
  UPDATE_SETTINGS_PROFILE_PHOTO_PRIVACY = 'UPDATE_SETTINGS_PROFILE_PHOTO_PRIVACY',
  UNDO_SETTINGS_PROFILE_PHOTO_PRIVACY = 'UNDO_SETTINGS_PROFILE_PHOTO_PRIVACY',
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTINGS_PROFILE_PHOTO_PRIVACY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: albumsUrl,
    urlPattern: 'https://www.facebook.com/.*sk=photos_albums',
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    action: async (machineContext, userContext, additionalMachineData) => {
      const { initialValues: storedInitialValues, updateStoredInitialValues } =
        userContext
      const { resumeData } = additionalMachineData
      const processedPhotoIds = new Set(resumeData.data.processedPhotoIds || [])

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        INITIAL_VALUE_KEY,
        OLD_INITIAL_VALUE_KEY,
      )

      await waitForAlbumsToLoad({ timeoutMs: 5000 })

      // Check first (and exit early) if no profile pictures album
      if (!hasAlbum('Profile pictures')) {
        updateStoredInitialValues(
          { [INITIAL_VALUE_KEY]: null },
          FBRecommendationKey.FB_PROFILE_PHOTO_PRIVACY,
        )
        return undefined
      }

      goToAlbum('Profile pictures')

      await scrollToBottom()

      const photoIds = getVisiblePhotoUrls()
        .map((url) => {
          try {
            return new URL(url, 'https://www.facebook.com').searchParams.get(
              'fbid',
            )
          } catch {
            return null
          }
        })
        .filter((id: string | null) => id && !processedPhotoIds.has(id))

      const profilePhotoGatheredSettings = initialValues || {}
      for (const photoId of photoIds) {
        const waitForPhotoPage = promises.getUrlChangePromise()
        await clickOnPhotoById(photoId)
        await waitForPhotoPage

        // Wait for photo page to load (a little hacky)
        await promises.wait(400)

        const value = await gatherEditPrivacyIconValue()
        if (value) {
          profilePhotoGatheredSettings[photoId] = value

          await updateStoredInitialValues(
            {
              [INITIAL_VALUE_KEY]: profilePhotoGatheredSettings,
            },
            FBRecommendationKey.FB_PROFILE_PHOTO_PRIVACY,
          )
        }

        processedPhotoIds.add(photoId)
        resumeData.set({ processedPhotoIds: [...processedPhotoIds] })

        await promises.wait(400)

        const waitForReturn = promises.getUrlChangePromise()
        window.history.back()
        await waitForReturn
      }
      // catch the case where no photos are found
      await updateStoredInitialValues(
        { [INITIAL_VALUE_KEY]: profilePhotoGatheredSettings },
        FBRecommendationKey.FB_PROFILE_PHOTO_PRIVACY,
      )

      return true
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTINGS_PROFILE_PHOTO_PRIVACY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: albumsUrl,
    urlPattern: 'https://www.facebook.com/.*sk=photos_albums',
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    action: async (machineContext, userContext, additionalMachineData) => {
      const {
        incrementTotalCount,
        updateStatisticsLastCount,
        initialValues: storedInitialValues,
        isDry,
      } = userContext

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        INITIAL_VALUE_KEY,
        OLD_INITIAL_VALUE_KEY,
      )

      if (initialValues === null || initialValues === undefined) {
        throw new Error('No initial values found.')
      }

      await waitForAlbumsToLoad({ timeoutMs: 5000 })

      // Check first (and exit early) if no profile pictures album
      if (!hasAlbum('Profile pictures')) {
        return undefined
      }

      goToAlbum('Profile pictures')

      let profilePictureIds = Object.keys(initialValues || {})

      const { resumeData } = additionalMachineData
      const processedPhotoIds = new Set(resumeData.data.processedPhotoIds || [])
      profilePictureIds = profilePictureIds.filter(
        (id) => !processedPhotoIds.has(id),
      )

      await scrollToBottom()

      for (const currentPhotoId of profilePictureIds) {
        const waitForPhotoPage = promises.getUrlChangePromise()
        await clickOnPhotoById(currentPhotoId)
        await waitForPhotoPage

        // Wait for photo page to load (a little hacky)
        await promises.wait(400)

        await setEditPrivacyIconValueAsStrictOrStricter(
          selectAudienceUtils.FRIENDS,
          selectAudienceUtils.ONLY_ME,
          isDry,
        )

        processedPhotoIds.add(currentPhotoId)
        resumeData.set({ processedPhotoIds: [...processedPhotoIds] })

        await incrementTotalCount(FBRecommendationKey.FB_PROFILE_PHOTO_PRIVACY)
        await updateStatisticsLastCount(
          FBRecommendationKey.FB_PROFILE_PHOTO_PRIVACY,
          processedPhotoIds.size,
        )

        await promises.wait(400)

        const waitForReturn = promises.getUrlChangePromise()
        window.history.back()
        await waitForReturn
      }

      return true
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTINGS_PROFILE_PHOTO_PRIVACY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: albumsUrl,
    urlPattern: 'https://www.facebook.com/.*sk=photos_albums',
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    action: async (machineContext, userContext, additionalMachineData) => {
      const { initialValues: storedInitialValues, isDry } = userContext

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        INITIAL_VALUE_KEY,
        OLD_INITIAL_VALUE_KEY,
      )

      if (initialValues === null || initialValues === undefined) {
        throw new Error('No initial values found.')
      }

      await waitForAlbumsToLoad({ timeoutMs: 5000 })

      // Check first (and exit early) if no profile pictures album
      if (!hasAlbum('Profile pictures')) {
        return undefined
      }

      goToAlbum('Profile pictures')

      let profilePictureIds = Object.keys(initialValues || {})

      const { resumeData } = additionalMachineData
      const processedPhotoIds = new Set(resumeData.data.processedPhotoIds || [])
      profilePictureIds = profilePictureIds.filter(
        (id) => !processedPhotoIds.has(id),
      )

      await scrollToBottom()

      for (const currentPhotoId of profilePictureIds) {
        const waitForPhotoPage = promises.getUrlChangePromise()
        await clickOnPhotoById(currentPhotoId)
        await waitForPhotoPage

        // Wait for photo page to load (a little hacky)
        await promises.wait(400)

        const initialSetting = initialValues[getPhotoIdFromCurrentUrl()]

        if (initialSetting) {
          await setEditPrivacyIconValueExact(initialSetting, isDry)
        }

        processedPhotoIds.add(currentPhotoId)

        resumeData.set({ processedPhotoIds: [...processedPhotoIds] })
        await promises.wait(400)

        const waitForReturn = promises.getUrlChangePromise()
        window.history.back()
        await waitForReturn

        promises.wait(100)
      }

      return true
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
